import { useEffect, useMemo, useState, useRef } from 'react';
import { useNavigate, useParams } from 'react-router';
import { goApi } from '../../../services';
import { Dialog } from '@mui/material';
// @ts-ignore
import { Calendar, momentLocalizer } from 'react-big-calendar';
import moment from 'moment';
import { isScheduleLive, isScheduleUpcoming } from '../../../class-utilities';
import { ScheduleForm } from './ScheduleForm';
import { LoadingSection } from '../../../common/LoadingSection';

const localizer = momentLocalizer(moment);

const CustomEvent = ({ event }: any) => {
    const renderVideoIcon = (videoType: string | undefined) => {
        if (videoType?.toLowerCase() === 'youtube') {
            return (
                <svg
                    width="28"
                    height="20"
                    viewBox="0 0 24 24"
                    xmlns="http://www.w3.org/2000/svg"
                    style={{ display: 'inline-block', verticalAlign: 'middle', marginRight: 6 }}
                >
                    <rect x="1" y="4" width="22" height="16" rx="4" fill="#FF0000" />
                    <polygon points="10,8 16,12 10,16" fill="#fff" />
                </svg>
            );
        }

        if (videoType?.toLowerCase() === 'secure_v2') {
            return (
                <svg
                    width="28"
                    height="20"
                    viewBox="0 0 24 24"
                    xmlns="http://www.w3.org/2000/svg"
                    style={{ display: 'inline-block', verticalAlign: 'middle', marginRight: 6 }}
                >
                    <rect x="1" y="4" width="22" height="16" rx="3" fill="#1a1a1a" />
                    <polygon points="9,8 9,16 16,12" fill="#fff" opacity="0.7" />
                    <rect x="15" y="12" width="6" height="6" rx="1" fill="#e0e0e0" />
                    <path
                        d="M 16 12 Q 16 10 18 10 Q 20 10 20 12"
                        stroke="#e0e0e0"
                        strokeWidth="1.5"
                        fill="none"
                    />
                    <circle cx="18" cy="14.5" r="0.8" fill="#1a1a1a" />
                </svg>
            );
        }

        return null;
    };

    return (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, paddingRight: 6 }}>
            <div
                style={{
                    flex: 1,
                    overflow: 'hidden',
                    whiteSpace: 'nowrap',
                    textOverflow: 'ellipsis',
                }}
            >
                <div
                    style={{ fontWeight: 600, fontSize: 12, display: 'flex', alignItems: 'center' }}
                >
                    {renderVideoIcon(event.videoType)}
                    <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {event.title || event.name}
                    </span>
                </div>
            </div>
        </div>
    );
};

export const BatchScheduleCalender = ({ batch, refreshKey }: any) => {
    const [selectSlotStartDate, setSelectSlotStartDate] = useState(null as any);
    const navigate = useNavigate();
    const { id } = useParams();
    const [schedules, setSchedules] = useState<any[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<any>(null);
    const [currentView, setCurrentView] = useState<'day' | 'week' | 'month'>('day');
    const [currentDate, setCurrentDate] = useState<Date>(() => new Date());
    const [dateRange, setDateRange] = useState<{ start: Date; end: Date } | null>(() => {
        const today = new Date();
        return { start: today, end: today };
    });
    const isViewChangingRef = useRef(false);

    const fetchSchedules = async (view?: 'day' | 'week' | 'month', start?: Date, end?: Date) => {
        if (!id) return;
        try {
            setIsLoading(true);
            setError(null);

            let url = `/batches/schedules/${id}/all-schedule`;
            const params = new URLSearchParams();

            if (view === 'day' && start) {
                const dateParam = moment(start).format('YYYY-MM-DD');
                params.append('date', dateParam);
            } else if ((view === 'week' || view === 'month') && start && end) {
                const startParam = moment(start).format('YYYY-MM-DD');
                const endParam = moment(end).format('YYYY-MM-DD');
                params.append('startDate', startParam);
                params.append('endDate', endParam);
            }

            const queryString = params.toString();
            if (queryString) {
                url += `?${queryString}`;
            }

            const response = await goApi.get(url);
            setSchedules(response?.data?.items || []);
        } catch (err: any) {
            setError(err);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        if (dateRange) {
            fetchSchedules(currentView, dateRange.start, dateRange.end);
        } else {
            const today = new Date();
            fetchSchedules('day', today, today);
        }
    }, [id, refreshKey]);

    const events = useMemo(() => {
        if (!Array.isArray(schedules)) return [];
        return schedules?.map(schedule => ({
            ...schedule,
            name: schedule.title,
            title: schedule.title,
            startTime: new Date(schedule.startTime),
            endTime: new Date(schedule.endTime),
            videoType: schedule?.video?.type,
        }));
    }, [schedules]);

    const handleViewChange = (view: any) => {
        isViewChangingRef.current = true;
        setCurrentView(view);
        if (dateRange) {
            let start = dateRange.start;
            let end = dateRange.end;

            if (view === 'week') {
                start = moment(currentDate).startOf('week').toDate();
                end = moment(currentDate).endOf('week').toDate();
            } else if (view === 'month') {
                start = moment(currentDate).startOf('month').toDate();
                end = moment(currentDate).endOf('month').toDate();
            } else {
                start = currentDate;
                end = currentDate;
            }

            setDateRange({ start, end });
            fetchSchedules(view, start, end);
        }
    };

    const handleRangeChange = (range: any, view?: any) => {
        let start: Date;
        let end: Date;
        let detectedView: 'day' | 'week' | 'month';

        if (Array.isArray(range)) {
            start = range[0];
            end = range[range.length - 1];
            detectedView = 'week';
        } else if (range.start && range.end) {
            start = range.start;
            end = range.end;
            detectedView = 'month';
        } else {
            start = range;
            end = range;
            detectedView = 'day';
        }

        setCurrentDate(start);
        setDateRange({ start, end });

        if (isViewChangingRef.current) {
            isViewChangingRef.current = false;
            return;
        }

        const actualView = view || detectedView;
        fetchSchedules(actualView, start, end);
    };

    return (
        <div>
            <Dialog
                open={!!selectSlotStartDate}
                onClose={(_refresh: boolean) => {
                    setSelectSlotStartDate(null);
                }}
            >
                <ScheduleForm
                    templateId={batch?.templateId}
                    batchId={id}
                    startTime={selectSlotStartDate?.start}
                    endTime={selectSlotStartDate?.end}
                    onClose={(_refresh: boolean) => {
                        if (_refresh) {
                            if (dateRange) {
                                fetchSchedules(currentView, dateRange.start, dateRange.end);
                            } else {
                                fetchSchedules();
                            }
                        }
                        setSelectSlotStartDate(null);
                    }}
                />
            </Dialog>
            {isLoading ? (
                <LoadingSection />
            ) : error ? (
                <div>Error loading schedules</div>
            ) : (
                <>
                    <Calendar
                        localizer={localizer}
                        defaultView={'day'}
                        date={currentDate}
                        onNavigate={(date: Date) => setCurrentDate(date)}
                        view={currentView}
                        onView={handleViewChange}
                        onRangeChange={handleRangeChange}
                        selectable={true}
                        components={{
                            event: CustomEvent,
                            month: { event: CustomEvent },
                            week: { event: CustomEvent },
                            day: { event: CustomEvent },
                            agenda: { event: CustomEvent },
                        }}
                        onSelectSlot={(slotInfo: any) => {
                            setSelectSlotStartDate(slotInfo);
                        }}
                        eventPropGetter={(event: any) => {
                            if (event.draft) {
                                return {
                                    style: {
                                        border: '2px solid #888888',
                                        backgroundColor: 'rgba(0,0,0,0.2)',
                                    },
                                };
                            }
                            if (isScheduleLive(event)) {
                                if (!event.videoId) {
                                    return {
                                        style: {
                                            backgroundImage:
                                                'radial-gradient(circle, black 2px, transparent 2px), radial-gradient(circle, black 2px, transparent 2px)',
                                            backgroundSize: '30px 30px',
                                            backgroundColor: 'red',
                                            backgroundPosition: '0 0, 15px 15px',
                                        },
                                    };
                                }
                                return {
                                    style: {
                                        backgroundColor: 'red',
                                    },
                                };
                            }
                            if (isScheduleUpcoming(event)) {
                                if (!event.videoId) {
                                    return {
                                        style: {
                                            backgroundImage:
                                                'radial-gradient(circle, black 2px, transparent 2px), radial-gradient(circle, black 2px, transparent 2px)',
                                            backgroundSize: '30px 30px',
                                            backgroundColor: 'cornflowerblue',
                                            backgroundPosition: '0 0, 15px 15px',
                                        },
                                    };
                                }
                                return {
                                    style: {
                                        backgroundColor: 'cornflowerblue',
                                    },
                                };
                            }
                            if (!event.videoId) {
                                return {
                                    style: {
                                        backgroundImage:
                                            'radial-gradient(circle, black 2px, transparent 2px), radial-gradient(circle, black 2px, transparent 2px)',
                                        backgroundSize: '30px 30px',
                                        backgroundColor: '#888888',
                                        backgroundPosition: '0 0, 15px 15px',
                                    },
                                };
                            }
                            return {
                                style: {
                                    backgroundColor: '#888888',
                                },
                            };
                        }}
                        events={[...events]}
                        startAccessor="startTime"
                        messages={{
                            agenda: 'Schedule',
                        }}
                        onSelectEvent={(event: any) => {
                            if (event.id) {
                                navigate(
                                    `/batch_schedules/${event.id}/show?templateId=${batch?.templateId}`,
                                );
                            } else {
                                setSelectSlotStartDate({
                                    start: event.startTime,
                                    end: event.endTime,
                                });
                            }
                        }}
                        endAccessor="endTime"
                        style={{ height: '80vh' }}
                    />
                </>
            )}
        </div>
    );
};
